import React, { Component, createContext, useContext } from "react";
import { Snackbar, Alert } from "@mui/material";
import { keyframes } from "@mui/material/styles";

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-8px); }
  50% { transform: translateX(8px); }
  75% { transform: translateX(-4px); }
`;

type MessageContextValue = {
  showMessage: (text: string, severity?: "info" | "success" | "error" | "warning") => void;
  showError: (text: string) => void;
  showSuccess: (text: string) => void;
  clearError: () => void;
};

type MessageSeverity = "info" | "success" | "error" | "warning";

type Message = {
  text: string;
  severity: MessageSeverity;
};

type MessageDisplayProps = {
  message: Message | null;
  messageVersion: number;
  onClose: () => void;
};

type MessageProviderProps = {
  children: React.ReactNode;
};

type MessageProviderState = {
  message: Message | null;
  messageVersion: number;
  hasError: boolean;
};

// Context for showing messages
const MessageContext = createContext<MessageContextValue | undefined>(undefined);

// Hook to use the message context
export const useMessage = () => {
  const context = useContext(MessageContext);
  if (!context) {
    throw new Error("useMessage must be used within MessageProvider");
  }
  return context;
};

// Message display component
function MessageDisplay({ message, messageVersion, onClose }: MessageDisplayProps) {
  if (!message) return null;

  // Check if the message contains traceback formatting
  const hasTraceback = message.text && message.text.includes('Traceback:');

  return (
    <Snackbar
      open={!!message}
      autoHideDuration={message.severity === "error" ? null : 6000}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    >
      <Alert
        key={messageVersion}
        onClose={onClose} 
        severity={message.severity} 
        sx={{ 
          width: "100%",
          maxWidth: hasTraceback ? "800px" : "100%",
          animation: message.severity === "error" ? `${shake} 250ms ease-in-out` : "none",
          "@media (prefers-reduced-motion: reduce)": {
            animation: "none",
          },
        }}
      >
        {hasTraceback ? (
          <pre style={{ 
            whiteSpace: "pre-wrap", 
            wordBreak: "break-word",
            fontFamily: "monospace",
            fontSize: "0.875rem",
            margin: 0,
          }}>
            {message.text}
          </pre>
        ) : (
          <span style={{ whiteSpace: "pre-line" }}>{message.text}</span>
        )}
      </Alert>
    </Snackbar>
  );
}

// Error Boundary with Message Provider
class MessageProvider extends Component<MessageProviderProps, MessageProviderState> {
  constructor(props: MessageProviderProps) {
    super(props);
    this.state = {
      message: null,
      messageVersion: 0,
      hasError: false,
    };
  }

  static getDerivedStateFromError(error: Error, previousState: MessageProviderState): MessageProviderState {
    return {
      hasError: true,
      messageVersion: previousState.messageVersion + 1,
      message: {
        text: error.message || "An unexpected error occurred",
        severity: "error",
      },
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Error caught by boundary:", error, errorInfo);
  }

  showMessage = (text: string, severity: MessageSeverity = "info") => {
    this.setState((previousState) => ({
      message: { text, severity },
      messageVersion: previousState.messageVersion + 1,
      hasError: false,
    }));
  };

  showError = (text: string) => {
    this.showMessage(text, "error");
  };

  showSuccess = (text: string) => {
    this.showMessage(text, "success");
  };

  clearError = () => {
    this.setState((previousState) => {
      if (!previousState.message || previousState.message.severity !== "error") {
        return null;
      }

      return { message: null, hasError: false };
    });
  };

  handleCloseMessage = () => {
    this.setState({ message: null, hasError: false });
  };

  render() {
    const contextValue = {
      showMessage: this.showMessage,
      showError: this.showError,
      showSuccess: this.showSuccess,
      clearError: this.clearError,
    };

    return (
      <MessageContext.Provider value={contextValue}>
        {this.props.children}
        <MessageDisplay
          message={this.state.message}
          messageVersion={this.state.messageVersion}
          onClose={this.handleCloseMessage}
        />
      </MessageContext.Provider>
    );
  }
}

export default MessageProvider;
